import { CreateSLAResponseDto, SLAViewDto } from '@issuemanager/api-client';
import {
  ErrorFloatingCard,
  SidePanel,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledDivider,
} from '@issuemanager/apps/client/components';
import { ISSUE_SUBTYPE_NONE_VALUE } from '@issuemanager/constants';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSLAForm } from '../hooks';
import { DividerMiddleButton } from './divider-middle-button';
import { ReminderDaysField } from './reminder-days-field';
import { SLAPanelFooter } from './sla-panel-footer';

type Props = {
  setOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  sla: SLAViewDto | null;
  onSuccess?: (value: CreateSLAResponseDto) => void;
};

export function SLAPanel(props: Props) {
  const { sla, isOpen, setOpen, onSuccess } = props;
  const { t } = useTranslation(['common', 'settings']);

  const {
    form,
    onSubmit,
    fields,
    reminderDaysFields,
    handleAddReminderDays,
    isLoading: formIsLoading,
    resetForm,
    errors,
    hasErrors,
    cleanErrors,
    issueTypesQuery,
  } = useSLAForm({
    id: sla?.id,
    onSuccess: (v) => {
      setOpen(false);
      onSuccess?.(v);
    },
    open: isOpen,
  });

  const [remindersExpanded, setRemindersExpanded] = useState(false);

  useEffect(() => {
    if (reminderDaysFields.length) {
      setRemindersExpanded(true);
    }
  }, [reminderDaysFields.length]);

  useEffect(() => {
    if (!sla) return;
    form.reset({
      issueTypeId: sla.issueType.id,
      issueSubtypeId: sla.issueSubtype?.id ?? ISSUE_SUBTYPE_NONE_VALUE,
      severity: sla.severity,
      slaDays: sla.slaDays,
      maxDueDate: sla.maxDueDate,
      escalationDays: sla.escalationDays,
      reminderDays: sla.reminderDays.map((r) => ({ value: r })),
    });
  }, [sla]);

  const handleSaveOrUpdate = () => {
    onSubmit();
  };

  return (
    <SidePanel
      open={isOpen}
      onClose={setOpen}
      error={issueTypesQuery.isError}
      title={
        sla
          ? t('settings:slaConfiguration.sidePanel.titleUpdate')
          : t('settings:slaConfiguration.sidePanel.titleCreate')
      }
      footer={
        <SLAPanelFooter
          hasChanged={form.formState.isDirty}
          formIsLoading={formIsLoading}
          cleanForm={resetForm}
          saveOrUpdate={handleSaveOrUpdate}
          mode={sla ? 'update' : 'create'}
        />
      }
    >
      <>
        {hasErrors && (
          <ErrorFloatingCard onClose={cleanErrors} errors={errors} />
        )}

        {formIsLoading ? (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            height='100%'
          >
            <CircularProgress />
          </Box>
        ) : (
          <form data-testid='form-add-sla'>
            <Stack spacing={3}>
              <Stack flexDirection='row' gap={1} flex={1} flexWrap='wrap'>
                {fields.issueType}
                {fields.issueSubtype}
              </Stack>
              <Stack spacing={3} flex={1}>
                {fields.severity}
              </Stack>
              <Stack spacing={3} flex={1}>
                {fields.slaDays}
                {fields.maxDueDate}
                {fields.escalationDays}
              </Stack>
              <Stack spacing={3} flex={1}>
                <Stack width='100%'>
                  <StyledAccordion
                    data-atlas-alignment='end'
                    data-testid='action'
                    expanded={remindersExpanded}
                    onChange={() => setRemindersExpanded(!remindersExpanded)}
                  >
                    <StyledAccordionSummary
                      aria-controls='panel-content'
                      id='panel-header'
                      disabled={!reminderDaysFields.length}
                    >
                      <Typography fontWeight='medium' marginRight={1}>
                        {t('settings:slaConfiguration.sidePanel.reminders')}
                      </Typography>
                      <StyledDivider flexItem mb={10} />
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                      <Stack spacing={4} mb={4}>
                        {reminderDaysFields.map((field) => (
                          <ReminderDaysField key={field.id} field={field} />
                        ))}
                      </Stack>
                    </StyledAccordionDetails>
                  </StyledAccordion>
                  <DividerMiddleButton
                    formIsLoading={formIsLoading}
                    handleAddReminderDays={handleAddReminderDays}
                  />
                </Stack>
              </Stack>
            </Stack>
          </form>
        )}
      </>
    </SidePanel>
  );
}
